import React from 'react';
import { IconMissing } from '../../../../../assets/icons';
import {
  BinarySensorTypeInternal,
  ChannelTypeInternal,
  DeviceConnectionState,
  LightToggleValueInternal,
  SensorTypeInternal,
  SwitchStateInternal,
} from '../../../../../data-access/gql-types/graphql';
import { brightnessIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/brightness';
import { coverIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/cover';
import { floodIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/flood';
import { gateIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/gate';
import { humidityIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/humidity';
import { inputIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/input';
import { lightIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/light';
import { meterIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/meter';
import { movementIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/movement';
import { optimizerIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/optimizer';
import { pressureIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/pressure';
import { reedIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/reed';
import { switchIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/switch';
import { temperatureIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/temperature';
import { windIcons } from '../../../modules/channel-configuration/components/icon-edit/icons/wind';
import { useChannelsStateStore } from '../../../store/channels-state';
import {
  ChannelCoverStateType,
  ChannelType,
  ChannelLightStateType,
  ChannelSwitchStateType,
  ChannelGateStateType,
  ChannelGateType,
} from '../../../types';
import { findChannelIcon } from '../../../utils/channels/helpers';

type ChannelIconProps = {
  channel: ChannelType;
  statusOn?: boolean;
};

export const ChannelIcon: React.FC<ChannelIconProps> = ({ channel, statusOn }) => {
  const { id, data, iconName } = channel;
  const state = useChannelsStateStore((s) => s.channelState[id]);

  switch (data.type) {
    case ChannelTypeInternal.Switch: {
      return findChannelIcon(
        switchIcons(statusOn || (state as ChannelSwitchStateType)?.lavvaState === SwitchStateInternal.On),
        iconName,
      );
    }
    case ChannelTypeInternal.Light:
      return findChannelIcon(
        lightIcons(statusOn || (state as ChannelLightStateType)?.toggleValue === LightToggleValueInternal.On),
        iconName,
      );
    case ChannelTypeInternal.Blind:
      return findChannelIcon(coverIcons((state as ChannelCoverStateType)?.lavvaStatePosition || 0));
    case ChannelTypeInternal.Gate:
      return findChannelIcon(
        gateIcons((channel.data as ChannelGateType)?.gateKind, (state as ChannelGateStateType)?.position || 0, {
          iconSun: !!(state as ChannelGateStateType)?.dailyModeStatus,
        }),
        iconName,
      );
    case ChannelTypeInternal.Meter:
      return findChannelIcon(
        meterIcons(statusOn || state?.deviceConnectionState !== DeviceConnectionState.Disconnected),
        iconName,
      );
    case ChannelTypeInternal.Optimizer:
      return findChannelIcon(
        optimizerIcons(statusOn || state?.deviceConnectionState !== DeviceConnectionState.Disconnected),
        iconName,
      );
    case ChannelTypeInternal.Sensor:
      switch (data.sensorType) {
        case SensorTypeInternal.Brightness:
          return findChannelIcon(brightnessIcons(), iconName);
        case SensorTypeInternal.Humidity:
          return findChannelIcon(humidityIcons(), iconName);
        case SensorTypeInternal.Pressure:
          return findChannelIcon(pressureIcons(), iconName);
        case SensorTypeInternal.Temperature:
          return findChannelIcon(temperatureIcons(), iconName);
        case SensorTypeInternal.Wind:
          return findChannelIcon(windIcons(), iconName);
        default:
          return <IconMissing />;
      }
    case ChannelTypeInternal.BinarySensor:
      switch (data.sensorType) {
        case BinarySensorTypeInternal.Flood:
          return findChannelIcon(floodIcons(), iconName);
        case BinarySensorTypeInternal.Movement:
          return findChannelIcon(movementIcons(), iconName);
        case BinarySensorTypeInternal.ReedSwitch:
          return findChannelIcon(reedIcons(), iconName);
        case BinarySensorTypeInternal.Input:
          return findChannelIcon(inputIcons(), iconName);
        default:
          return <IconMissing />;
      }
    default:
      return <IconMissing />;
  }
};
